import { createEntityRef, getEntityValueFromARef, normalizeKey, type EntityRef } from '../../../types';
import { NON_INDEXED_PROPERTY_STUB } from '../../../utils/entityPropertyUtils';
import { isARef } from '../../../utils/entityPropertyUtils';
import type { EntityUpsertRenderFieldContext } from './EntityUpsertRenderFieldContext';

export interface RenderFieldLocals {
    attr: Record<string, unknown>;
    name: string;
    label: string;
    attrRef: EntityRef;
    value: unknown;
    isStubValueInner: boolean;
    attrId: string | undefined;
    binding: EntityUpsertRenderFieldContext['schemaEntityBindings'][string] | undefined;
    isRequired: boolean;
    allowedValues: unknown[] | undefined;
    bindingRegex: string | undefined;
    isReadOnlyField: boolean;
    isFieldDisabled: boolean;
    isPossessiveType: boolean;
}

export function buildRenderFieldLocals(
    attr: Record<string, unknown>,
    ctx: EntityUpsertRenderFieldContext
): RenderFieldLocals | null {
    const name = (attr.name || attr.id || attr.value) as string | undefined;
    if (name == null || String(name).trim() === '') return null;
    const attrRef = createEntityRef(name);
    const label = (attr.label || name) as string;
    const value = getEntityValueFromARef(name, ctx.formData);
    const isStubValueInner =
        value === NON_INDEXED_PROPERTY_STUB ||
        (ctx.nonIndexedHasLengthCountNamesSet.has(normalizeKey(name)) && ctx.isEmptyForDeferLoad(value));
    const attrId = (attr.id ?? attr.name) as string | undefined;
    const binding = attrId
        ? ctx.schemaEntityBindings[attrId.toLowerCase()] ?? ctx.schemaEntityBindings[attrId]
        : undefined;
    const isRequired = binding ? binding.required : Boolean(attr.required);
    const allowedValues = binding?.allowedValues;
    const bindingRegex = binding?.regex;
    const isIdField = isARef(attrRef, 'Id') || isARef(attrRef, 'ObjectId');
    const isReadOnlyField =
        attr.readOnly === true ||
        binding?.readOnly === true ||
        isIdField ||
        isARef(attrRef, 'EntraId') ||
        isARef(attrRef, 'MemberTransitive');
    const managedType = (
        getEntityValueFromARef('ManagedType', ctx.formData) ?? ctx.existingEntity?.ManagedType ?? ''
    ).toString();
    const isExternalManaged =
        ctx.entityType?.toLowerCase() === 'group' && managedType?.toLowerCase() === 'external';
    const isFieldDisabled = ctx.loading || isReadOnlyField || isExternalManaged || ctx.isViewSurface;
    const typeAttr =
        ctx.isRequestApprovalType && ctx.schemaAttributes?.length
            ? ctx.schemaAttributes.find((a) => {
                  const n = (a.name ?? a.id ?? a.value ?? '').toString().trim();
                  return n && isARef(createEntityRef(n), 'Type');
              })
            : null;
    const typeFormKey = typeAttr ? (typeAttr.name ?? typeAttr.id ?? typeAttr.value ?? 'Type') : 'Type';
    const requestApprovalTypeValue = ctx.isRequestApprovalType
        ? (
              getEntityValueFromARef(typeFormKey as string, ctx.formData) ??
              ctx.formData?.Type ??
              ctx.formData?.type ??
              ''
          )
              .toString()
              .trim()
        : '';
    const isPossessiveType = requestApprovalTypeValue.toLowerCase() === 'possessive';

    return {
        attr,
        name,
        label,
        attrRef,
        value,
        isStubValueInner,
        attrId,
        binding,
        isRequired: Boolean(isRequired),
        allowedValues,
        bindingRegex,
        isReadOnlyField,
        isFieldDisabled,
        isPossessiveType,
    };
}

export function createContentTypeHelpers(
    ctx: Pick<EntityUpsertRenderFieldContext, 'isContent' | 'isEmailTemplate' | 'formData'>
) {
    const getContentType = (): string | null => {
        if (!ctx.isContent) return null;
        const contentType = getEntityValueFromARef('ContentType', ctx.formData);
        return contentType != null && typeof contentType === 'string' ? contentType.toLowerCase() : null;
    };

    const getBodyContentType = (): string | null => {
        if (ctx.isContent) {
            return getContentType();
        }
        if (ctx.isEmailTemplate) {
            const contentType = getEntityValueFromARef('ContentType', ctx.formData);
            if (contentType != null && typeof contentType === 'string') {
                return contentType.toLowerCase();
            }
            return 'text/html';
        }
        return null;
    };

    const isHtmlField = (fieldName: string, fieldLabel: string): boolean => {
        const lowerName = fieldName.toLowerCase();
        const lowerLabel = fieldLabel.toLowerCase();
        if ((ctx.isContent || ctx.isEmailTemplate) && lowerName === 'body') return false;
        return (
            lowerName === 'body' ||
            lowerName === 'html' ||
            lowerName === 'htmlbody' ||
            lowerLabel.includes('html') ||
            lowerLabel.includes('body')
        );
    };

    return { getContentType, getBodyContentType, isHtmlField };
}
